
/**
 * ImagoButton - the product's only button.
 *
 * Heights come from the design system's control scale: 32 / 36 / 40px. 28px is
 * the hard floor for anything interactive, and is reserved for icon-only
 * controls, where there is no label to crowd.
 *
 * Press does not transform - active repeats hover, nothing scales.
 * Disabled is opacity .5 plus not-allowed.
 *
 * @param {Object} props
 * @param {'primary' | 'primaryGradient' | 'secondary' | 'danger' | 'ghost' | 'brand'} [props.variant='primary']
 *        primary = the action. primaryGradient is reserved for Send - one per
 *        screen. brand = orange, identity only, never an in-product action.
 * @param {'sm' | 'md' | 'lg'} [props.size='md'] - 32 / 36 / 40px tall
 * @param {boolean} [props.disabled=false]
 * @param {boolean} [props.loading=false] - swaps the left icon for a spinner
 * @param {boolean} [props.fullWidth=false]
 * @param {React.ReactNode} [props.leftIcon]
 * @param {React.ReactNode} [props.rightIcon]
 * @param {string} [props.shortcut] - trailing 11px <kbd> hint, e.g. "⌘↵"
 * @param {string} [props.className] - additional CSS classes
 * @param {React.ReactNode} props.children
 * @param {Function} [props.onClick]
 */
export function ImagoButton({
  variant = 'primary',
  size = 'md',
  disabled = false,
  loading = false,
  fullWidth = false,
  leftIcon,
  rightIcon,
  shortcut,
  className = '',
  children,
  onClick,
  type = 'button',
  ...rest
}) {
  const baseStyles = `
    inline-flex items-center justify-center whitespace-nowrap font-medium rounded-md
    border transition-colors duration-base ease-standard
    focus:outline-none
    disabled:opacity-50 disabled:cursor-not-allowed
  `;

  // Hover lightens on dark surfaces; solid fills are the exception and go
  // darker. Active repeats hover.
  const variantStyles = {
    primary: `
      bg-primary text-white border-transparent
      hover:bg-primary-hover active:bg-primary-hover
    `,
    // Reserved for Send. Using it twice on one screen destroys the signal.
    primaryGradient: `
      bg-gradient-to-br from-blue-500 to-blue-600 text-white border-transparent
      shadow-theme-lg
      hover:from-blue-600 hover:to-blue-700
      active:from-blue-600 active:to-blue-700
    `,
    secondary: `
      bg-transparent text-foreground border-border
      hover:bg-card hover:border-border-secondary
      active:bg-card
    `,
    danger: `
      bg-danger text-white border-transparent
      hover:bg-danger-hover active:bg-danger-hover
    `,
    ghost: `
      bg-transparent text-foreground border-transparent
      hover:bg-card-hover active:bg-card-hover
    `,
    brand: `
      bg-brand text-white border-transparent
      hover:bg-brand-hover active:bg-brand-hover
    `,
  };

  // 32 / 36 / 40px tall with 12/16/20px of horizontal air. The type step rises
  // with the control.
  //
  // Responsive step-down: `lg` (the primary-action size) reads oversized on a
  // phone, so below `md` (768px) it renders at the `md` scale — 36px, text-ui,
  // 16px air — and only grows to the full 40px on tablet and up. This is why
  // screens don't each have to special-case button size for mobile.
  const sizeStyles = {
    sm: 'h-control-sm px-3 text-xs gap-1.5',
    md: 'h-control-md px-4 text-ui gap-2',
    lg: 'h-control-md md:h-control-lg px-4 md:px-5 text-ui md:text-sm gap-2',
  };

  const LoadingSpinner = () => (
    <svg
      className="animate-imago-spin h-4 w-4 flex-shrink-0"
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      aria-hidden="true"
    >
      <circle
        className="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="4"
      />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
      />
    </svg>
  );

  const combinedClassName = `
    ${baseStyles}
    ${variantStyles[variant] || variantStyles.primary}
    ${sizeStyles[size] || sizeStyles.md}
    ${fullWidth ? 'flex w-full' : ''}
    ${className}
  `.replace(/\s+/g, ' ').trim();

  return (
    <button
      type={type}
      className={combinedClassName}
      disabled={disabled || loading}
      onClick={onClick}
      {...rest}
    >
      {loading ? (
        <LoadingSpinner />
      ) : leftIcon ? (
        <span className="flex flex-shrink-0">{leftIcon}</span>
      ) : null}
      {children}
      {!loading && rightIcon && (
        <span className="flex flex-shrink-0">{rightIcon}</span>
      )}
      {shortcut && (
        <kbd className="ml-0.5 font-sans text-label tracking-label opacity-[.65]">
          {shortcut}
        </kbd>
      )}
    </button>
  );
}

/**
 * ImagoIconButton - square icon-only control.
 *
 * The product wrote this recipe inline three separate times (ImagoHeader,
 * ImagoThemeToggle, ImagoModal's close button) with p-1 / p-2 / p-2.5. Same
 * recipe, named once.
 *
 * @param {Object} props
 * @param {'sm' | 'md' | 'lg'} [props.size='md'] - 28 / 32 / 36px square
 * @param {string} props.label - required; becomes both aria-label and title
 * @param {boolean} [props.active=false]
 * @param {boolean} [props.disabled=false]
 * @param {string} [props.className]
 * @param {React.ReactNode} props.children - a Lucide glyph
 */
export function ImagoIconButton({
  size = 'md',
  label,
  active = false,
  disabled = false,
  className = '',
  children,
  onClick,
  type = 'button',
  ...rest
}) {
  // One step tighter than a labelled button at every size: an icon has no
  // label beside it, so the same box reads as roomier.
  const sizeStyles = {
    sm: 'w-control-xs h-control-xs',
    md: 'w-control-sm h-control-sm',
    lg: 'w-control-md h-control-md',
  };

  const stateStyles = active
    ? 'bg-card text-primary'
    : 'bg-transparent text-foreground-secondary hover:bg-card-hover hover:text-foreground';

  const combinedClassName = `
    inline-flex items-center justify-center flex-shrink-0
    rounded-md border border-transparent
    transition-colors duration-base ease-standard
    focus:outline-none
    disabled:opacity-50 disabled:cursor-not-allowed
    ${sizeStyles[size] || sizeStyles.md}
    ${stateStyles}
    ${className}
  `.replace(/\s+/g, ' ').trim();

  return (
    <button
      type={type}
      aria-label={label}
      title={label}
      disabled={disabled}
      onClick={onClick}
      className={combinedClassName}
      {...rest}
    >
      {children}
    </button>
  );
}

export default ImagoButton;
